"use client";

import { type RefObject, useLayoutEffect, useRef } from "react";

/** Smooth height on a clipping shell when inner content changes (tab/case switch). */
export function useShowcaseShellHeight(
  shellRef: RefObject<HTMLDivElement | null>,
  innerRef: RefObject<HTMLDivElement | null>,
  prefersReducedMotion: boolean,
  ...remeasureDeps: unknown[]
) {
  const heightReadyRef = useRef(false);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    const inner = innerRef.current;
    if (!shell || !inner) return;

    const syncHeight = () => {
      if (prefersReducedMotion) {
        shell.style.height = "";
        shell.removeAttribute("data-height-ready");
        heightReadyRef.current = false;
        return;
      }

      const nextHeight = inner.offsetHeight;
      if (!heightReadyRef.current) {
        shell.style.height = `${nextHeight}px`;
        shell.dataset.heightReady = "true";
        heightReadyRef.current = true;
        return;
      }

      shell.style.height = `${nextHeight}px`;
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(inner);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remeasure when tab/case content changes
  }, [prefersReducedMotion, shellRef, innerRef, ...remeasureDeps]);
}
